import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import {
    IInteractionMgr,
    IInteractionData,
    AuthenticateAction,
    AuthToken,
    ISessionVerificationResponse,
    ISessionVerificationError,
    IFraudData,
    IAuditInteractionData,
    IInteractionMgrConfig,
    ISessionVerificationData,
    IInteractionMgrAdditionalConfig
} from '@nab/x-types';
import { EXPONENTIAL_BACKOFF, FRAUD_RETRY_CODES, MAX_RETRY_COUNT } from './constants';
import { InteractionSessionEvents } from './types';

export const InteractionTypes = {
    [InteractionSessionEvents.USER_INTERACTION]: 'Interaction',
    [InteractionSessionEvents.SESSION_VERIFICATION]: 'Session Verification',
    [InteractionSessionEvents.AUDIT_INTERACTION]: 'Audit Interaction'
};

/**
 * [[include:packages/core/sdk-core/src/BaseInteractionManager/README.md]]
 */
class BaseInteractionManager implements IInteractionMgr {
    public config: IInteractionMgrConfig;

    private tokenProvider: AuthenticateAction;

    private httpClientInstance: AxiosInstance = null;

    public axiosRetryConfig: any;

    public additionalConfig: IInteractionMgrAdditionalConfig;

    /**
     *
     * @param {IInteractionMgrConfig} config Configuration used to set the Endpoint URL and Bearer Token value used to communicate with the Interaction Service API
     */
    constructor(config: IInteractionMgrConfig, axiosRetryConfig?: any, additionalConfig?: IInteractionMgrAdditionalConfig) {
        this.httpClientInstance = axios.create();
        this.config = config;
        this.additionalConfig = additionalConfig;
        this.axiosRetryConfig = {
            retries: MAX_RETRY_COUNT,
            retryDelay: (retryCount) => {
                const backoff = EXPONENTIAL_BACKOFF.INITIAL_SECONDS * 1000 * retryCount;
                return Math.min(backoff, EXPONENTIAL_BACKOFF.MAX_SECONDS * 1000);
            },
            retryCondition: (error) => {
                return FRAUD_RETRY_CODES.includes(error.response.status);
            },
            ...axiosRetryConfig
        };
    }

    protected validateData<T extends IFraudData>(data: T, interactionType: InteractionSessionEvents) {
        if (!this.tokenProvider) {
            throw new Error('The Token Provider is not provided');
        }

        const { endpoint, sessionEndpoint, auditEndpoint } = this.config;
        if (interactionType === InteractionSessionEvents.USER_INTERACTION && !endpoint) {
            throw new Error('Missing interaction Endpoint URL.');
        } else if (interactionType === InteractionSessionEvents.SESSION_VERIFICATION && !sessionEndpoint) {
            throw new Error('Missing session verification Endpoint URL.');
        } else if (interactionType === InteractionSessionEvents.AUDIT_INTERACTION && !auditEndpoint) {
            throw new Error('Missing audit interaction Endpoint URL.');
        }
        if (!data.eventData.traceId) {
            throw new Error('Traceid is missing on your payload.');
        }
    }

    public async handleInteraction(data: IInteractionData): Promise<boolean> {
        return this.runCheck(this.config.endpoint, data, InteractionSessionEvents.USER_INTERACTION);
    }

    public async handleAuditInteraction(data: IAuditInteractionData): Promise<boolean> {
        return this.runCheck(this.config.auditEndpoint, data, InteractionSessionEvents.AUDIT_INTERACTION);
    }

    public async handleSessionVerification(data: ISessionVerificationData): Promise<ISessionVerificationResponse | ISessionVerificationError> {
        return this.runCheck(this.config.sessionEndpoint, data, InteractionSessionEvents.SESSION_VERIFICATION);
    }

    public setTokenProvider(authenticateProvider: AuthenticateAction): void {
        this.tokenProvider = authenticateProvider;
    }

    private async runCheck<T extends IFraudData>(endpoint: string, data: T, interactionType: InteractionSessionEvents): Promise<any> {
        axiosRetry(this.httpClientInstance, this.axiosRetryConfig);
        try {
            const tokenAuthenticateValue = await this.generateTokenAuthenticate(data, interactionType);
            const { eventData, sessionData } = data;
            const { traceId, eventType, ...restOfEventData } = eventData;
            const updatedEventType = this.appendInteractionPrefix(eventType);
            const response = await this.httpClientInstance.post<any>(
                endpoint,
                {
                    eventData: {
                        eventType: updatedEventType,
                        ...restOfEventData
                    },
                    sessionData
                },
                {
                    withCredentials: true,
                    headers: {
                        'Content-Type': 'application/json',
                        'x-nab-traceid': `interaction=${traceId}`,
                        authorization: `Bearer ${tokenAuthenticateValue}`
                    }
                }
            );
            if (response.status === 204) {
                return true;
            }
            return response.data;
        } catch (e) {
            console.error(`Error occurred while calling ${InteractionTypes[interactionType]}:`, e.message);
            if ([InteractionSessionEvents.USER_INTERACTION, InteractionSessionEvents.AUDIT_INTERACTION].includes(interactionType)) {
                return false;
            }
            if (e.response?.data?.errorId) {
                return { ...e.response.data, status: e.response.status };
            }
            throw e;
        }
    }

    private async generateTokenAuthenticate<T extends IFraudData>(data: T, interactionType: InteractionSessionEvents) {
        this.validateData(data, interactionType);
        const { tokenValue }: AuthToken = await this.tokenProvider();
        return tokenValue;
    }

    private appendInteractionPrefix(eventType: string) {
        // if eventType already starts with the interaction prefix, do not add it again
        const bShouldAppend = this.additionalConfig?.interactionPrefix?.length > 0 && !eventType.startsWith(this.additionalConfig.interactionPrefix);

        if (!bShouldAppend) {
            return eventType;
        }

        return `${this.additionalConfig.interactionPrefix}_${eventType}`;
    }
}

export { BaseInteractionManager };
